/**
 * Pacote 4, bloco 3 (I16; aprovado em 17/10/2026) — PDF da Ordem de Locação no template real da OCF (marca, blocos
 * numerados, tipografia, margens e rodapé), com "Página X de Y" na impressão. Ordem dos blocos: Parceiro, Inttrix,
 * Faturamento e entrega, Condições negociadas (ANTES dos itens), Equipamentos e componentes, Observações,
 * Referências do Cliente e Documentos anexos. Ao Parceiro vão a margem bruta negociada (valor e %) e o total do
 * contrato; nunca o líquido do parceiro, a comissão líquida estimada nem controles internos.
 */
import type { ReactNode } from 'react';
import { dataBr } from '../../../backend/src/compartilhado/oportunidades.ts';
import { textoReajuste } from '../../../backend/src/compartilhado/comercial.ts';
import { useCarga } from './ui.tsx';
import { api } from '../api.ts';
import { Bloco, DocumentoInttrix, Icone, linhasEndereco, Pares } from './documentos/DocumentoInttrix.tsx';
import { textoHistorico } from './FichaOrdem.tsx';
import { regraDaOl } from './FichaOl.tsx';
import type { Acompanhamento } from './Locacao.tsx';
import { moeda2, pctNum } from './proposta/formatos.ts';
import type { DocCompleto } from './Ordens.tsx';

const Tabela = ({ cab, linhas, num = [], teste }: { cab: string[]; linhas: ReactNode[][]; num?: number[]; teste?: string }) => (
  <div className="d-corpo d-tabela">
    <table className="d-tab" data-teste={teste}>
      <thead>
        <tr>
          {cab.map((c, i) => (
            <th key={c} className={num.includes(i) ? 'd-num' : undefined}>
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {linhas.length ? (
          linhas.map((l, i) => (
            <tr key={i}>
              {l.map((x, k) => (
                <td key={k} className={num.includes(k) ? 'd-num' : undefined}>
                  {x || '—'}
                </td>
              ))}
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={cab.length}>Não informado</td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

export function OlPdf({ doc }: { doc: DocCompleto }) {
  const carga = useCarga(() => api.get<Acompanhamento>(`/api/ordens/${doc.numero}/locacao`), [doc.numero]);
  const p = doc.pedidoCompleto;
  const c = p.conteudo;
  const l = c.locacao!;
  const e = doc.entidade;
  const cad = p.cadastro;
  const fat = c.faturamento;
  const ent = c.entrega;
  const r = regraDaOl(l);
  const refs = carga.dados?.referencias;
  const margem = r.unica ? `${moeda2(l.comissaoTotal)} (${pctNum(r.percentual)} sobre o total do contrato, paga uma única vez)` : `${moeda2(l.comissaoParcela)} por ${r.id === 'por_parcela' ? 'parcela' : 'mês'} (${pctNum(r.percentual)}) · ${moeda2(l.comissaoTotal)} no contrato`;
  return (
    <DocumentoInttrix
      titulo="ORDEM DE LOCAÇÃO"
      subtitulo="Documento destinado ao Parceiro de Locação."
      colunasIdent={2}
      ident={[
        ['OL', doc.numero],
        ['Data', dataBr(doc.dataCriacao)],
        ['Proposta de origem', `${p.proposta.numero} | V${p.versao}`, true],
      ]}
      empresa={c.empresa ?? null}
      rascunho={doc.situacao === 'em_conferencia' ? 'EM CONFERÊNCIA — NÃO EMITIDA' : doc.situacao === 'cancelada' ? 'CANCELADA' : undefined}
      avisoRascunho={doc.situacao === 'em_conferencia' ? 'Em conferência: o documento definitivo é o emitido.' : 'Documento cancelado.'}
      voltar={`/ordens/${doc.numero}`}
      teste="pdf-ol"
      paginado
    >
      <Bloco n={1} titulo="PARCEIRO" teste="pdf-ol-parceiro">
        <div className="d-corpo d-par">
          <Icone nome="predio" grande />
          <div className="d-grade d-div-v" style={{ gridTemplateColumns: '1fr 1fr', flex: 1 }}>
            <Pares pares={[['Locadora', e?.razao ?? '—', true], ['CNPJ', e?.cnpj ?? '—'], ['Código de revenda', e?.codigoRevenda || '—']]} />
            <Pares pares={[['Contato', e?.contato || '—'], ['E-mail', e?.email || '—'], ['Telefone', e?.telefone || '—']]} />
          </div>
        </div>
      </Bloco>
      <Bloco n={2} titulo="INTTRIX" teste="pdf-ol-inttrix">
        <div className="d-corpo d-par">
          <Icone nome="predio" grande />
          <div className="d-grade d-div-v" style={{ gridTemplateColumns: '1fr 1fr', flex: 1 }}>
            <Pares pares={[['Razão social', c.empresa?.razao ?? 'a configurar (P-18)', true], ['CNPJ', c.empresa?.cnpj ?? 'a configurar (P-18)']]} />
            <Pares pares={[['Responsável', p.responsavel.nome], ['E-mail', p.responsavel.email]]} />
          </div>
        </div>
      </Bloco>
      <Bloco n={3} titulo="FATURAMENTO E ENTREGA" teste="pdf-ol-faturamento">
        <div className="d-corpo d-par">
          <div className="d-grade d-div-v" style={{ gridTemplateColumns: '1fr 1fr', flex: 1 }}>
            <Pares
              pares={[
                ['Faturamento', fat.razao, true],
                ['CNPJ', fat.cnpj],
                ['Inscrição estadual', textoHistorico(cad.faturamento.ie, cad.dataReferencia)],
                ['Endereço', linhasEndereco(fat.endereco).join(' — ')],
                ['Contato', [fat.contato, fat.telefone].filter(Boolean).join(' · ') || '—'],
                ['E-mail', textoHistorico(cad.faturamento.email, cad.dataReferencia, false)],
                ['OC do Cliente', p.ocsVigentes.map((x) => x.numero).join(' · ') || 'não informada'],
              ]}
            />
            <Pares
              pares={[
                ['Entrega e instalação', ent.razao, true],
                ['CNPJ', ent.cnpj],
                ['Inscrição estadual', textoHistorico(cad.entrega.ie, cad.dataReferencia)],
                ['Endereço', linhasEndereco(ent.endereco).join(' — ')],
                ['Contato', [ent.contato, ent.telefone].filter(Boolean).join(' · ') || '—'],
              ]}
            />
          </div>
        </div>
      </Bloco>
      <Bloco n={4} titulo="CONDIÇÕES NEGOCIADAS" teste="pdf-ol-condicoes">
        <div className="d-corpo d-par">
          <div className="d-grade d-div-v" style={{ gridTemplateColumns: '1fr 1fr', flex: 1 }}>
            <Pares pares={[['Prazo', `${l.prazoMeses} meses`], ['Periodicidade', `${l.periodicidade} · ${l.cobrancas} cobranças`], ['Pagamento', l.formaPagamento || 'não informado'], ['Reajuste', textoReajuste(l.reajuste)]]} />
            <Pares pares={[[l.periodicidade === 'Mensal' ? 'Mensalidade' : 'Parcela', moeda2(l.clienteParcela), true], ['Total do contrato', moeda2(l.totalContrato), true], ['Regra de margem', `${pctNum(r.percentual)} ${r.nome}`], ['Margem bruta', margem, true]]} />
          </div>
        </div>
      </Bloco>
      <Bloco n={5} titulo="EQUIPAMENTOS E COMPONENTES" teste="pdf-ol-itens" quebra>
        <Tabela cab={['PN', 'Descrição', 'Qtd.']} num={[2]} linhas={l.componentes.map((x) => [x.pn || '—', x.descricao, String(x.quantidadeAceita)])} teste="pdf-ol-tabela-itens" />
      </Bloco>
      <Bloco n={6} titulo="OBSERVAÇÕES OPERACIONAIS" teste="pdf-ol-observacoes">
        <div className="d-corpo">
          <p className="d-v sem-margem">{[doc.observacoes, l.observacoes].filter(Boolean).join(' ') || 'Sem observações adicionais.'}</p>
        </div>
      </Bloco>
      <Bloco n={7} titulo="REFERÊNCIAS DO CLIENTE" teste="pdf-ol-referencias" quebra>
        {!refs ? (
          <div className="d-corpo">
            <p className="d-v">Carregando…</p>
          </div>
        ) : (
          <>
            <div className="d-corpo">
              <div className="d-r d-forte">7.1 Procuradores por unidade</div>
            </div>
            <Tabela cab={['Unidade (CNPJ)', 'Nome', 'E-mail']} linhas={refs.procuradores.map((x) => [x.unidade, x.nome, x.email])} teste="pdf-ol-procuradores" />
            <div className="d-corpo">
              <div className="d-r d-forte">7.2 Testemunhas</div>
            </div>
            <Tabela cab={['Nome', 'E-mail']} linhas={refs.testemunhas.map((x) => [x.nome, x.email])} />
            <div className="d-corpo">
              <div className="d-r d-forte">7.3 Referências comerciais</div>
            </div>
            <Tabela cab={['Empresa', 'Telefone', 'E-mail']} linhas={refs.comerciais.map((x) => [x.empresa, x.telefone, x.email])} />
            <div className="d-corpo">
              <div className="d-r d-forte">7.4 Referência bancária</div>
            </div>
            <Tabela cab={['Banco', 'Contato', 'Telefone', 'E-mail']} linhas={refs.bancaria ? [[refs.bancaria.banco, refs.bancaria.contato, refs.bancaria.telefone, refs.bancaria.email]] : []} />
          </>
        )}
      </Bloco>
      <Bloco n={8} titulo="DOCUMENTOS ANEXOS" teste="pdf-ol-anexos">
        <Tabela cab={['#', 'Documento', 'Arquivo', 'Observação']} linhas={(refs?.contratoSocial ?? []).map((d, i) => [String(i + 1), 'Contrato social', d.nome, 'Arquivo separado, acompanha esta OL'])} />
      </Bloco>
    </DocumentoInttrix>
  );
}
